import { useClerk, useSession } from "@clerk/clerk-react";
import type { Employee, PermissionKey } from "@shared/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { orpc, rpcClient } from "@/lib/rpc";

interface AuthUser {
  id: string;
  email: string;
  isSuperAdmin: boolean;
  isManager: boolean;
  isExternal: boolean;
  isImpersonating?: boolean;
  /** True when the user has been granted the labs SpiceDB role explicitly.
   *  Independent of isSuperAdmin/isManager: a labs-role user might be neither.
   *  Used by client-side gates that mirror the server's requireLabsAccess
   *  (e.g., evaluation-settings page). The server is still the source of
   *  truth — every /api/labs/** request goes through requireLabsAccess. */
  hasLabsAccess: boolean;
  hasOnboardingTemplateAccess: boolean;
  isBuddyOfActiveOnboarding: boolean;
  permissions: PermissionKey[];
}

interface AuthContextType {
  user: AuthUser | null;
  employee: Employee | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isManager: boolean;
  isExternal: boolean;
  isImpersonating: boolean;
  hasLabsAccess: boolean;
  hasOnboardingTemplateAccess: boolean;
  isBuddyOfActiveOnboarding: boolean;
  permissions: PermissionKey[];
  hasPermission: (key: PermissionKey) => boolean;
  impersonationBlocked: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { signOut } = useClerk();
  const { session: clerkSession } = useSession();
  const bridgeAttempted = useRef(false);
  const [impersonationBlocked, setImpersonationBlocked] = useState(false);

  const { data, isLoading } = useQuery({
    ...orpc.auth.me.queryOptions(),
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
    // Bootstrap "who am I" query. A 401 here just means "not logged in" — which is
    // the expected state for anonymous visitors and candidates on the public offer
    // pages (/offer/:token etc.), where AuthProvider still wraps the tree. Opt out
    // of the global error handler so this expected 401 doesn't fire the misleading
    // "Sessão expirada" toast. Genuine session-expiry on authenticated pages still
    // surfaces via the 401s of the page's own (non-bootstrap) queries.
    meta: { skipGlobalError: true },
  });

  // Sync Clerk ↔ Express session state.
  // Triggers bridge callback when:
  // 1. No Express session but Clerk has session (e.g., F5 during impersonation)
  // 2. Express session exists but Clerk impersonation state diverges (e.g., admin
  //    was logged in, then started impersonating via Clerk Dashboard and refreshed)
  useEffect(() => {
    if (isLoading || bridgeAttempted.current || !clerkSession) return;

    const clerkIsImpersonating = !!(clerkSession as any).actor;
    const expressIsImpersonating = !!data?.user?.isImpersonating;
    const needsBridge = !data || clerkIsImpersonating !== expressIsImpersonating;

    if (!needsBridge) return;

    bridgeAttempted.current = true;
    clerkSession.getToken().then(async (token) => {
      if (!token) return;
      try {
        const authData = await rpcClient.auth.clerkCallback({ sessionToken: token });
        queryClient.setQueryData(orpc.auth.me.queryOptions().queryKey, authData as any);
      } catch (err: any) {
        if (err?.status === 403 || err?.code === "FORBIDDEN") {
          setImpersonationBlocked(true);
        }
      }
    });
  }, [isLoading, data, clerkSession, queryClient]);

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await rpcClient.auth.logout();
      try {
        await signOut();
      } catch {
        // Clerk session may not exist (OTP-only login), ignore
      }
    },
    onSuccess: () => {
      queryClient.setQueryData(orpc.auth.me.queryOptions().queryKey, null as any);
      queryClient.clear();
      window.location.href = "/login";
    },
  });

  const permissions = data?.user?.permissions || [];
  const isSuperAdmin = data?.user?.isSuperAdmin || false;

  const value: AuthContextType = {
    user: data?.user || null,
    employee: data?.employee || null,
    isLoading: isLoading || (!data && !!clerkSession && !bridgeAttempted.current),
    isAuthenticated: !!data?.user,
    isSuperAdmin,
    isManager: data?.user?.isManager || false,
    isExternal: data?.user?.isExternal || false,
    isImpersonating: data?.user?.isImpersonating || false,
    hasLabsAccess: data?.user?.hasLabsAccess || false,
    hasOnboardingTemplateAccess: data?.user?.hasOnboardingTemplateAccess || false,
    isBuddyOfActiveOnboarding: data?.user?.isBuddyOfActiveOnboarding || false,
    permissions,
    hasPermission: (key: PermissionKey) => isSuperAdmin || permissions.includes(key),
    impersonationBlocked,
    logout: () => logoutMutation.mutate(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
